// src/app/components/sso-settings-card.tsx
//
// Admin-only card to enable/disable and configure SSO (Entra ID / Okta / any
// OIDC IdP) at runtime. Backed by GET/PUT /auth/admin/sso. Mounted under
// Global Settings → Platform → Single sign-on, beside the per-customer
// connections (sso-connections-card.tsx).
//
// Local username/password login is never affected by these settings, so an
// admin can always get back in even if SSO is misconfigured.

import { useEffect, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { authApi, type SsoAdminConfig, type SsoConfigUpdate } from "@/api/auth";

export function SsoSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [issuer, setIssuer] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");   // write-only; blank = unchanged
  const [secretSet, setSecretSet] = useState(false);
  const [redirectUri, setRedirectUri] = useState("");
  const [scopes, setScopes] = useState("");
  const [adminGroup, setAdminGroup] = useState("");

  useEffect(() => {
    let alive = true;
    authApi.getSsoAdminConfig()
      .then((cfg: SsoAdminConfig) => {
        if (!alive) return;
        setEnabled(cfg.enabled);
        setIssuer(cfg.issuer || "");
        setClientId(cfg.client_id || "");
        setSecretSet(cfg.client_secret_set);
        setRedirectUri(cfg.redirect_uri || "");
        setScopes(cfg.scopes || "openid profile email");
        setAdminGroup(cfg.admin_group || "");
      })
      .catch((e) => alive && setError(e?.message || "Failed to load SSO settings"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const payload: SsoConfigUpdate = {
        enabled,
        issuer: issuer.trim(),
        client_id: clientId.trim(),
        redirect_uri: redirectUri.trim(),
        scopes: scopes.trim(),
        admin_group: adminGroup.trim(),
      };
      // Only send the secret if the admin actually typed one (blank = keep current).
      if (clientSecret) payload.client_secret = clientSecret;

      const cfg = await authApi.updateSsoAdminConfig(payload);
      setEnabled(cfg.enabled);
      setSecretSet(cfg.client_secret_set);
      setClientSecret("");
      setSaved(true);
    } catch (e: any) {
      setError(e?.message || "Failed to save SSO settings");
    } finally {
      setSaving(false);
    }
  };

  const field = (
    label: string, value: string, onChange: (v: string) => void,
    placeholder = "", type = "text",
  ) => (
    <div style={{ marginBottom: 14 }}>
      <Label style={{ display: "block", marginBottom: 6, fontSize: "var(--text-sm)" }}>
        {label}
      </Label>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        disabled={saving}
      />
    </div>
  );

  if (loading) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: 24,
        color: "var(--muted-foreground)", fontFamily: "var(--font-sans)",
      }}>
        <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
        Loading SSO settings…
      </div>
    );
  }

  return (
    <div style={{
      border: "1px solid var(--border)", borderRadius: 12,
      padding: 24, background: "var(--card)", maxWidth: 640,
      fontFamily: "var(--font-sans)",
    }}>
      <h3 style={{ margin: "0 0 4px", fontSize: "var(--text-lg)", color: "var(--foreground)" }}>
        Single Sign-On (SSO)
      </h3>
      <p style={{ margin: "0 0 18px", fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
        Let users sign in with your identity provider (Microsoft Entra ID, Okta, or any
        OIDC IdP). Username/password login keeps working regardless of this setting.
      </p>

      {error && (
        <Alert variant="destructive" style={{ marginBottom: 16 }}>
          <AlertCircle style={{ width: 16, height: 16 }} />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {saved && (
        <Alert style={{ marginBottom: 16 }}>
          <CheckCircle2 style={{ width: 16, height: 16 }} />
          <AlertDescription>SSO settings saved.</AlertDescription>
        </Alert>
      )}

      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 18, padding: "12px 14px", borderRadius: 8,
        background: "var(--input-background)", border: "1px solid var(--border)",
      }}>
        <div>
          <div style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)" }}>
            Enable SSO
          </div>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            Shows the “Sign in with SSO” button and accepts IdP logins.
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} disabled={saving} />
      </div>

      {field("Issuer URL", issuer, setIssuer,
        "https://login.microsoftonline.com/<tenant-id>/v2.0")}
      {field("Client ID", clientId, setClientId, "Application (client) ID")}
      {field(
        secretSet ? "Client secret (set — leave blank to keep)" : "Client secret (optional for SPA)",
        clientSecret, setClientSecret,
        secretSet ? "••••••••" : "Only for confidential/web apps", "password",
      )}
      {field("Redirect URI", redirectUri, setRedirectUri,
        `${window.location.origin}/`)}
      {field("Scopes", scopes, setScopes, "openid profile email")}
      {field("Admin group (optional)", adminGroup, setAdminGroup,
        "Group claim that grants admin — leave blank for none")}

      <div style={{ marginTop: 8 }}>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <><Loader2 className="animate-spin" style={{ width: 16, height: 16, marginRight: 8 }} />Saving…</>
          ) : "Save SSO settings"}
        </Button>
      </div>
    </div>
  );
}
